/**
 * Utilities for opening planning documents.
 *
 * The default action opens a City Scope document viewer, which keeps cached
 * previews and public image/PDF links reliable. Edinburgh Idox PDFs can still
 * be opened through a separate explicit council-session action.
 */

import { toast } from '../components/ui/use-toast';
import { councilSlugToShortCode, getCouncilConfig, isLegacyCouncil } from '../lib/councils';

const COUNCIL_ROOT = 'https://citydev-portal.edinburgh.gov.uk/idoxpa-web';

type PlanningDocumentRef = {
  doc_link?: string | null;
  document_url?: string | null;
  pdf_url?: string | null;
  url?: string | null;
  link?: string | null;
  thumbnail_url?: string | null;
  thumbnail?: string | null;
  source_desc?: string | null;
  title?: string | null;
  name?: string | null;
};

type PlanningDocumentViewerOptions = {
  appLink?: string | null;
  previewUrl?: string | null;
  reference?: string | null;
  council?: string | null;
  title?: string | null;
  sourceDesc?: string | null;
};

export const PLANNING_DOCUMENT_VIEWER_EVENT = 'cityscope:open-planning-document';

export type PlanningDocumentViewerDetail = {
  documentUrl: string;
  viewerUrl: string;
  appLink?: string | null;
  previewUrl?: string | null;
  reference?: string | null;
  council?: string | null;
  title?: string | null;
  sourceDesc?: string | null;
};

function decodePathSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

function getFirstQueryValue(params: URLSearchParams, key: string): string | null {
  const value = params.get(key);
  return value && value.trim() ? value.trim() : null;
}

export function parsePlanningDocumentViewerUrl(
  asPath?: string | null,
): PlanningDocumentViewerDetail | null {
  if (!asPath) return null;

  let parsed: URL;
  try {
    parsed = new URL(asPath, 'https://city-scope.local');
  } catch {
    return null;
  }

  const segments = parsed.pathname.split('/').filter(Boolean).map(decodePathSegment);
  const routeIndex = segments.findIndex((segment) =>
    /^(?:planning-application|planning-updates)$/i.test(segment),
  );
  if (routeIndex < 0) return null;

  const documentUrl = getFirstQueryValue(parsed.searchParams, 'url');
  if (!documentUrl || !isHttpUrl(documentUrl)) return null;

  const documentIndex = segments.findIndex(
    (segment, index) => index > routeIndex && /^document$/i.test(segment),
  );
  const routeEndIndex = documentIndex >= 0 ? documentIndex : segments.length;
  const routeName = segments[routeIndex]?.toLowerCase();
  const routeSegments = segments.slice(routeIndex + 1, routeEndIndex);
  const routeCouncil = routeName === 'planning-updates'
    ? getCouncilConfig(routeSegments[0])
    : null;
  const referenceSegments = routeCouncil ? routeSegments.slice(1) : routeSegments;
  const referenceFromPath = referenceSegments.join('/');
  const reference = getFirstQueryValue(parsed.searchParams, 'ref') || referenceFromPath || null;
  if (!reference) return null;

  return {
    documentUrl,
    viewerUrl: `${parsed.pathname}${parsed.search}`,
    appLink: getFirstQueryValue(parsed.searchParams, 'app'),
    previewUrl: getFirstQueryValue(parsed.searchParams, 'preview'),
    reference,
    council: routeCouncil?.slug || getFirstQueryValue(parsed.searchParams, 'council'),
    title: getFirstQueryValue(parsed.searchParams, 'title'),
    sourceDesc: getFirstQueryValue(parsed.searchParams, 'sourceDesc'),
  };
}

export function isPlanningDocumentViewerUrl(asPath?: string | null): boolean {
  return Boolean(parsePlanningDocumentViewerUrl(asPath));
}

type PrimedDocumentOptions = PlanningDocumentViewerOptions & {
  primeDelayMs?: number;
};

function extractAppRef(fileUrl: string): string | null {
  const m = /\/([0-9]{2}_[0-9]{5}_[A-Z]+)[-_]/.exec(fileUrl);
  return m ? m[1] : null;
}

function isHttpUrl(value: unknown): value is string {
  return typeof value === 'string' && /^https?:\/\//i.test(value.trim());
}

function firstHttpUrl(...values: unknown[]): string | null {
  for (const value of values) {
    if (isHttpUrl(value)) return value.trim();
  }
  return null;
}

function firstText(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

function encodeReferencePath(reference: string): string {
  return reference
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean)
    .map(encodeURIComponent)
    .join('/');
}

function buildApplicationPath(reference: string, council?: string | null): string {
  const encodedReference = encodeReferencePath(reference);
  const councilConfig = getCouncilConfig(council);
  if (councilConfig && !isLegacyCouncil(councilConfig.slug)) {
    return `/planning-updates/${councilSlugToShortCode(councilConfig.slug)}/${encodedReference}`;
  }
  return `/planning-application/${encodedReference}`;
}

function extractCurrentPlanningApplicationRoute(): { reference: string; council?: string | null } | null {
  if (typeof window === 'undefined') return null;

  const pathSegments = window.location.pathname
    .split('/')
    .map((segment) => {
      try {
        return decodeURIComponent(segment);
      } catch {
        return segment;
      }
    })
    .filter(Boolean);

  const routeIndex = pathSegments.findIndex((segment) => /^(?:planning-application|planning-updates)$/i.test(segment));
  if (routeIndex < 0) return null;

  const routeName = pathSegments[routeIndex]?.toLowerCase();
  const routeSegments = pathSegments.slice(routeIndex + 1);
  const routeCouncil = routeName === 'planning-updates'
    ? getCouncilConfig(routeSegments[0])
    : null;
  const referenceSegments = routeCouncil ? routeSegments.slice(1) : routeSegments;
  const documentIndex = referenceSegments.findIndex((segment) => /^document$/i.test(segment));
  const cleanSegments = documentIndex >= 0 ? referenceSegments.slice(0, documentIndex) : referenceSegments;
  const reference = cleanSegments.join('/').trim();
  return reference ? { reference, council: routeCouncil?.slug || null } : null;
}

export function getPlanningDocumentUrl(docRef?: PlanningDocumentRef | null, fallback?: unknown): string | null {
  return firstHttpUrl(
    fallback,
    docRef?.doc_link,
    docRef?.document_url,
    docRef?.pdf_url,
    docRef?.url,
    docRef?.link,
    docRef?.thumbnail_url,
    docRef?.thumbnail,
  );
}

export function getPlanningDocumentPreviewUrl(docRef?: PlanningDocumentRef | null, fallback?: unknown): string | null {
  return firstHttpUrl(fallback, docRef?.thumbnail_url, docRef?.thumbnail);
}

export function isIdoxDocumentUrl(url?: string | null): boolean {
  return typeof url === 'string' && /:\/\/citydev-portal\.edinburgh\.gov\.uk\/idoxpa-web\/files\//i.test(url);
}

export function buildPlanningDocumentViewerUrl(
  documentUrl: string,
  options: PlanningDocumentViewerOptions = {},
): string {
  const currentRoute = extractCurrentPlanningApplicationRoute();
  const reference = firstText(options.reference) || currentRoute?.reference;
  if (!reference) return documentUrl;
  const council = firstText(options.council) || currentRoute?.council || null;

  const params = new URLSearchParams({ url: documentUrl });
  const previewUrl = firstHttpUrl(options.previewUrl);
  const appLink = firstHttpUrl(options.appLink);
  const title = firstText(options.title, options.sourceDesc);

  if (previewUrl) params.set('preview', previewUrl);
  if (appLink) params.set('app', appLink);
  if (title) params.set('title', title);
  if (reference) params.set('ref', reference);

  return `${buildApplicationPath(reference, council)}/document?${params.toString()}`;
}

export function requestPlanningDocumentViewer(detail: PlanningDocumentViewerDetail): boolean {
  if (typeof window === 'undefined') return false;

  const event = new CustomEvent(PLANNING_DOCUMENT_VIEWER_EVENT, {
    detail,
    cancelable: true,
  });
  const notCancelled = window.dispatchEvent(event);
  return !notCancelled || event.defaultPrevented;
}

function openNewTab(
  url: string,
  { quiet = false, description }: { quiet?: boolean; description?: string } = {},
): Window | null {
  const win = window.open(url, '_blank', 'noopener,noreferrer');
  if (!win) {
    console.error('[openPlanningDoc] Pop-up blocker likely prevented opening URL:', url);
    return null;
  }

  if (!quiet) {
    toast({
      title: 'Document opened',
      description: description || 'The planning document or image should now be open in a new tab.',
      variant: 'default',
      duration: 2000,
    });
  }

  return win;
}

function buildDetailsUrl(ref: string): string {
  const keyVal = ref.replace(/\//g, '_').replace(/\s+/g, '');
  return `${COUNCIL_ROOT}/applicationDetails.do?activeTab=document&keyVal=${keyVal}`;
}

function buildIdoxDocumentsTabUrl(appLink: string, appRef: string): string {
  let primingUrl = appLink;
  if (!primingUrl.includes('keyVal=')) {
    console.warn('[openPlanningDoc] appLink does not contain keyVal. Constructing Idox details URL.', { appRef });
    primingUrl = buildDetailsUrl(appRef);
  }

  if (primingUrl.includes('activeTab=')) {
    return primingUrl.replace(/activeTab=[a-zA-Z]+/, 'activeTab=documents');
  }
  return `${primingUrl}${primingUrl.includes('?') ? '&' : '?'}activeTab=documents`;
}

function openHidden(url: string): Promise<void> {
  return new Promise((resolve) => {
    const win = window.open(
      url,
      '_blank',
      'noopener,noreferrer,width=10,height=10,left=-10000,top=-10000',
    );

    if (!win) {
      console.warn('[openHidden] Popup blocker likely prevented hidden window. Resolving immediately.');
      return resolve();
    }

    const done = () => {
      try {
        win.close();
      } catch {
        // Ignore close failures.
      }
      resolve();
    };

    win.addEventListener('load', () => setTimeout(done, 1000));
    setTimeout(done, 7000);
  });
}

function triggerImagePopupAndWaitForDocLink(
  thumbnailUrl: string,
  onImageClick: (url: string) => void,
  docRef: { doc_link: string },
  timeoutMs = 10000,
): Promise<string> {
  return new Promise((resolve, reject) => {
    onImageClick(thumbnailUrl);

    const startTime = Date.now();
    const checker = setInterval(() => {
      if (docRef.doc_link && docRef.doc_link.trim() !== '') {
        clearInterval(checker);
        resolve(docRef.doc_link);
        return;
      }

      if (Date.now() - startTime >= timeoutMs) {
        clearInterval(checker);
        reject(new Error(`Timeout waiting for doc_link in popup (${timeoutMs}ms)`));
      }
    }, 100);
  });
}

function waitForDocLink(docRef: { doc_link: string }, timeoutMs = 10000): Promise<string> {
  return new Promise((resolve, reject) => {
    if (docRef.doc_link && docRef.doc_link.trim() !== '') {
      resolve(docRef.doc_link);
      return;
    }

    const startTime = Date.now();
    const checker = setInterval(() => {
      if (docRef.doc_link && docRef.doc_link.trim() !== '') {
        clearInterval(checker);
        resolve(docRef.doc_link);
        return;
      }

      if (Date.now() - startTime >= timeoutMs) {
        clearInterval(checker);
        reject(new Error(`Timeout waiting for doc_link to load (${timeoutMs}ms)`));
      }
    }, 100);
  });
}

async function resolveDocumentLink(
  docLink: string,
  docRef?: PlanningDocumentRef,
  onImageClick?: (url: string) => void,
): Promise<string | null> {
  let finalDocLink = getPlanningDocumentUrl(docRef, docLink);
  if (finalDocLink) return finalDocLink;

  if (!docRef) return null;

  const fallbackImageUrl = getPlanningDocumentPreviewUrl(docRef);
  try {
    if (onImageClick && docRef.thumbnail_url) {
      finalDocLink = await triggerImagePopupAndWaitForDocLink(
        docRef.thumbnail_url,
        onImageClick,
        docRef as { doc_link: string },
      );
    } else {
      finalDocLink = await waitForDocLink(docRef as { doc_link: string });
    }
  } catch (error) {
    console.error('[openPlanningDoc] Error loading doc_link from API:', error);
    finalDocLink = fallbackImageUrl;
  }

  return finalDocLink;
}

export async function openPlanningDoc(
  docLink: string,
  appLink: string,
  docRef?: PlanningDocumentRef,
  onImageClick?: (url: string) => void,
  options: PlanningDocumentViewerOptions = {},
): Promise<void> {
  const finalDocLink = await resolveDocumentLink(docLink, docRef, onImageClick);

  if (!isHttpUrl(finalDocLink)) {
    toast({
      title: 'Invalid document link',
      description: 'The document link is not available or is invalid. It might still be processing.',
      variant: 'destructive',
      duration: 4000,
    });
    return;
  }

  const previewUrl = firstHttpUrl(options.previewUrl, getPlanningDocumentPreviewUrl(docRef));
  const resolvedAppLink = firstHttpUrl(options.appLink, appLink);
  const resolvedTitle = firstText(options.title, docRef?.source_desc, docRef?.title, docRef?.name);
  const resolvedReference = firstText(options.reference);
  const resolvedCouncil = firstText(options.council);
  const viewerUrl = buildPlanningDocumentViewerUrl(finalDocLink, {
    appLink: resolvedAppLink,
    previewUrl,
    reference: resolvedReference,
    council: resolvedCouncil,
    title: resolvedTitle,
    sourceDesc: options.sourceDesc,
  });

  const handledInApp = requestPlanningDocumentViewer({
    documentUrl: finalDocLink,
    viewerUrl,
    appLink: resolvedAppLink,
    previewUrl,
    reference: resolvedReference,
    council: resolvedCouncil,
    title: resolvedTitle,
    sourceDesc: options.sourceDesc,
  });

  if (handledInApp) return;

  openNewTab(viewerUrl, {
    description: 'The document is open in the City Scope viewer.',
  });
}

export async function openPrimedPlanningDoc(
  docLink: string,
  appLink: string,
  docRef?: PlanningDocumentRef,
  options: PrimedDocumentOptions = {},
): Promise<void> {
  const finalDocLink = await resolveDocumentLink(docLink, docRef);
  const appLinkIsValid = isHttpUrl(appLink);

  if (!isHttpUrl(finalDocLink)) {
    toast({
      title: 'Invalid document link',
      description: 'The council document link is not available or is invalid.',
      variant: 'destructive',
      duration: 4000,
    });
    return;
  }

  const appRef = extractAppRef(finalDocLink);
  if (!appRef || !appLinkIsValid || !isIdoxDocumentUrl(finalDocLink)) {
    openNewTab(finalDocLink, {
      description: 'The original document should now be open in a new tab.',
    });
    return;
  }

  const councilWindow = window.open('', '_blank', 'width=980,height=760');
  if (!councilWindow) {
    toast({
      title: 'Pop-up blocked',
      description: 'Allow pop-ups for City Scope to open the council document session.',
      variant: 'destructive',
      duration: 5000,
    });
    return;
  }

  try {
    councilWindow.document.write(`
      <!doctype html>
      <html>
        <head><title>Preparing council document...</title></head>
        <body style="font-family: system-ui, sans-serif; margin: 32px; color: #111827;">
          <h1 style="font-size: 18px;">Preparing council document...</h1>
          <p>City Scope is opening the council documents tab first, then this tab will move to the selected PDF.</p>
        </body>
      </html>
    `);
    councilWindow.document.close();
  } catch {
    // Ignore write failures; the window can still navigate.
  }

  const primerKey = `idox-primed-${appRef}`;
  const alreadyPrimed = sessionStorage.getItem(primerKey);
  const primeDelayMs = Math.max(1500, options.primeDelayMs ?? 4500);

  toast({
    title: alreadyPrimed ? 'Opening council document' : 'Preparing council document',
    description: alreadyPrimed
      ? 'Using the existing Idox session and opening the original PDF.'
      : 'Opening the council documents tab first, then the original PDF.',
    duration: 3500,
  });

  if (!alreadyPrimed) {
    councilWindow.location.href = buildIdoxDocumentsTabUrl(appLink, appRef);
    await new Promise(resolve => setTimeout(resolve, primeDelayMs));
    sessionStorage.setItem(primerKey, '1');
  }

  councilWindow.location.href = finalDocLink;
}

export const __testExports = {
  buildIdoxDocumentsTabUrl,
  openHidden,
  waitForDocLink,
  triggerImagePopupAndWaitForDocLink,
};
